/**
 * 작성자 : 홍선영
 * 날짜 : 2025.01.03
 * 기능 : (사내)사내관리-우수사원-우수사원 페이지 사원 목록
 */

import { t } from 'i18next';
import { Dispatch, RefObject, SetStateAction, useEffect, useRef, useState } from 'react';

import { SearchOptions } from '../../../assets/styles/SearchOptions';
import SelectBox from '../../../components/SelectBox';
import { LIST_COUNT } from '../../../_constants';
import { BtnGhost } from '../../../components/Button';
import { IComCdList } from 'customTypes';
import useSetListCount from '../../../utils/useSetListCount';
import { setComCdListState } from '../../../utils/setComCdListState';
import IssueGuide from '../../../components/IssueGuide';
import 'dayjs/locale/ko'; // 한국어 로케일 추가
import Input from '../../../components/Input';
import { useDebounce } from '../../../utils/useDebounce';
import useDateRange from '../../../hooks/useDateRange';
import RangePicker, { onChangeRangeInput } from '../../../components/RangePicker';
import type { IExellentUser } from './excellentEmployee';
import i18n from '../../../translation/i18n';
import TuiGrid from '../../../components/Table/TuiGrid';
import useTuiGridSize from '../../../hooks/useTuiGridSize';
import TuiGridWrapper from '../../../components/Table/TuiGridWrapper';
import { useOutletContext } from 'react-router-dom';
import Portal from '../../../components/Portal';
import SearchFilterModal from '../../../components/Modal/SearchFilterModal';

interface IList {
  data: IExellentUser[] | undefined;
  isLoading: boolean;
  isError: boolean;
  tableState: IExellentUser[] | null;
  setTableState: Dispatch<SetStateAction<IExellentUser[] | null>>;
  setRowState: Dispatch<SetStateAction<IExellentUser>>;
  isNewAdd: boolean;
  setIsNewAdd: Dispatch<SetStateAction<boolean>>;
  addBtnAction: () => void;
}

const List = ({ data, isLoading, isError, tableState, setTableState, setRowState, isNewAdd, setIsNewAdd, addBtnAction }: IList) => {
  const size = useOutletContext<any>();
  const [rowKey, setRowKey] = useState('');
  const [listCountComCdList, setListCountComCdList] = useState<IComCdList[]>([]); // 검색줄수 공통코드
  const { listCount, setListCount, patchUserMenuAPI } = useSetListCount(); // 검색줄수설정 훅
  const [searchOption, setSearchOption] = useState({ userName: '', cContents: '' });
  const wNameRef = useRef<HTMLInputElement>(null);
  const [initTableState, setInitTableState] = useState<IExellentUser[] | null>(data || null);
  const debouncedUserName = useDebounce(searchOption.userName);
  const debouncedContents = useDebounce(searchOption.cContents);
  const { dateRange, setDateRange, userInputDateRange, setUserInputDateRange, initiateDate } = useDateRange('month');
  const [visibleRangePicker, setVisibleRangePicker] = useState(false);
  const [openModal, setOpenModal] = useState<any>({ status: false, type: '' });
  const [columns, setColumns] = useState<any[]>([]);
  const componentRef = useRef<HTMLDivElement>(null);
  const { tuiHeight } = useTuiGridSize(componentRef);

  useEffect(() => {
    setColumns([
      {
        header: t('사원명'),
        name: 'userName',
        sortable: true,
        width: 100,
      },
      {
        header: t('내용'),
        name: 'cContents',
        sortable: true,
        minWidth: 200,
      },
      {
        header: t('부서명'),
        name: 'partCdName',
        sortable: true,
        filter: 'select',
        width: 120,
      },
      {
        header: t('직급'),
        name: 'positionName',
        sortable: true,
        filter: 'select',
        width: 120,
      },
      {
        header: t('적용일자'),
        name: 'cDate',
        align: 'center',
        sortable: true,
        width: 120,
      },
    ]);
  }, [i18n.language]);

  useEffect(() => {
    if (isNewAdd) setRowKey('');
  }, [isNewAdd]);

  useEffect(() => {
    if (data) {
      applyFilter(data);
      setInitTableState(data);
    }
  }, [data]);

  useEffect(() => {
    if (size.innerSize.W > 1024) handleSearch();
  }, [debouncedUserName, debouncedContents, userInputDateRange]);

  const handleSearch = (filterRef?: RefObject<HTMLDivElement>) => {
    if (filterRef) {
      // 모바일일 경우 검색 필터 모달 창 닫기
      filterRef.current?.classList.remove('active');
      setOpenModal({ status: false, type: 'filter' });
    }
    if (initTableState) applyFilter(initTableState);
  };

  const onEnterKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') handleSearch();
  };

  const convertToDate = (dateString: string) => {
    return new Date(`${dateString}T00:00:00`);
  };

  const applyFilter = (array: any[]) => {
    // 검색옵션 변경됐을 때 필터링 처리
    // 필터링 기준
    const filterOptions = {
      userName: searchOption.userName,
      cContents: searchOption.cContents,
    };

    // 필터링된 어레이 리턴, 대소문자구분X
    const filteredArray = array.filter((item: any) => {
      // 날짜 범위 필터
      let dateMatch = true;
      if (userInputDateRange.start && userInputDateRange.end) {
        const itemDate = convertToDate(item.cDate);
        const startDate = convertToDate(userInputDateRange.start);
        const endDate = convertToDate(userInputDateRange.end);
        dateMatch = itemDate >= startDate && itemDate <= endDate;
      }
      // 이름 필터
      const nameMatch = item.userName?.toLowerCase()?.includes(filterOptions.userName?.toLowerCase());
      // 내용 필터
      const contentsMatch = item.cContents?.toLowerCase()?.includes(filterOptions.cContents?.toLowerCase());

      return dateMatch && contentsMatch && nameMatch;
    });

    if (filteredArray.length > 0) setTableState(filteredArray.map((el: any, i) => ({ ...el, rowKey: i, sortKey: i })));
    else setTableState([]);
  };

  useEffect(() => {
    setComCdListState(LIST_COUNT, setListCountComCdList, false);
  }, []);

  const onClickInitiateSearchOption = () => {
    setSearchOption({ userName: '', cContents: '' });
    setRowState({
      cSeq: 0,
      userName: '',
      cCd: '',
      cDate: '',
      userId: '',
      cContents: '',
      partCd: '',
      partCdName: '',
      positionCd: '',
      positionName: '',
      wDate: '',
      writer: '',
      eDate: '',
      editor: '',
      rowKey: '',
    });
    setRowKey('');
    initiateDate();
    setIsNewAdd(true);
  };

  const onClickRow = (row: IExellentUser) => {
    if (row) {
      setRowState(row);
      setRowKey(row.rowKey.toString());
      setIsNewAdd(false);
    }
  };

  const onClickRangeInput = () => {
    if (!visibleRangePicker) setVisibleRangePicker(true);
  };

  const onClickAddNew = () => {
    addBtnAction();
    setRowState({
      cSeq: 0,
      userName: '',
      cCd: '',
      cDate: '',
      userId: '',
      cContents: '',
      partCd: '',
      partCdName: '',
      positionCd: '',
      positionName: '',
      wDate: '',
      writer: '',
      eDate: '',
      editor: '',
      rowKey: '',
    });
  };

  // 필터 버튼 클릭
  const onClickFilter = () => {
    setOpenModal({ status: true, type: 'filter' });
  };

  if (isError) return <IssueGuide />;

  return (
    <>
      <SearchOptions>
        {size.innerSize.W > 1024 ? (
          <>
            <div className='inputsWrapper'>
              <div className='inputForm-row-fit'>
                <span className='calendarLabel'>{t('적용일자')}</span>
                <div className='inputForm-col'>
                  <div className='flex-basic'>
                    <input
                      id='startInput'
                      type='text'
                      value={userInputDateRange.start}
                      // onMouseDown={onClickRangeInput}
                      onChange={(e) => onChangeRangeInput(e, 'start', setDateRange, setUserInputDateRange)}
                    />
                    <span className='wave'> ~ </span>
                    <input id='endInput' type='text' value={userInputDateRange.end} onChange={(e) => onChangeRangeInput(e, 'end', setDateRange, setUserInputDateRange)} />
                  </div>
                  {visibleRangePicker && (
                    <div className='rangePickerWrapper'>
                      <RangePicker state={dateRange} setState={setDateRange} setVisible={setVisibleRangePicker} />
                    </div>
                  )}
                </div>
                <button type='button' className='date-picker-btn' onClick={onClickRangeInput}>
                  <span className='material-symbols-rounded'>calendar_month</span>
                </button>
              </div>
              <div className='inputForm-row'>
                <div className='inputForm-col'>
                  <Input placeholder={t('사원명')} label='' type='text' id='userName' name='userName' state={searchOption} setState={setSearchOption} inputRef={wNameRef} />
                </div>
              </div>
              <div className='inputForm-row'>
                <div className='inputForm-col'>
                  <Input placeholder={t('내용')} label='' type='text' id='cContents' name='cContents' state={searchOption} setState={setSearchOption} inputRef={wNameRef} />
                </div>
              </div>
            </div>
            <div className='inputsWrapper'>
              <div className='secondSearchOption'>
                <div className='flex-basic textBtnGroup'>
                  <BtnGhost onClick={onClickInitiateSearchOption}>{t('초기화')}</BtnGhost>
                  <div className='searchResult'>
                    {t('총')}
                    <span>{tableState?.length ?? 0}</span>
                    {t('개')}
                  </div>
                  <div className='inputForm-row'>
                    <div className='inputForm-col withLabelComCf'>
                      <label htmlFor='useYn'>{t('보기 설정')}</label>
                      <SelectBox
                        options={listCountComCdList}
                        defaultOption={listCount.cdName}
                        state={listCount}
                        setState={setListCount}
                        stateKey={LIST_COUNT}
                        initiateKey={listCount[LIST_COUNT]}
                        setTableLinesAPI={patchUserMenuAPI}
                        optionHeight='height-sm'
                        rsearch
                      />
                    </div>
                  </div>
                </div>
                <div className='flex-basic iconBtnGroup'>
                  <BtnGhost onClick={() => onClickAddNew()}>
                    <span className='material-symbols-rounded'>add</span>
                    {t('등록')}
                  </BtnGhost>
                </div>
              </div>
            </div>
          </>
        ) : (
          <div className='filter-wrapper'>
            <div className='inputsWrapper'>
              <div className='inputForm-row' style={{ width: 'calc(100% - 3.5rem)' }}>
                <div className='inputForm-col'>
                  <Input
                    placeholder={t('사원명')}
                    label=''
                    type='text'
                    id='userName'
                    name='userName'
                    state={searchOption}
                    setState={setSearchOption}
                    inputRef={wNameRef}
                    onKeyDown={onEnterKeyDown}
                    ico='search'
                  />
                </div>
              </div>
              <BtnGhost onClick={onClickFilter} className='filter-btn'>
                <span className='material-symbols-rounded'>filter_alt</span>
              </BtnGhost>
            </div>
            <div className='inputsWrapper'>
              <div className='flex-basic iconBtnGroup'>
                <BtnGhost onClick={() => onClickAddNew()}>
                  <span className='material-symbols-rounded'>add</span>
                  {t('등록')}
                </BtnGhost>
              </div>
            </div>
          </div>
        )}
      </SearchOptions>
      {tableState && (
        <TuiGridWrapper componentRef={componentRef}>
          <TuiGrid data={tableState} columns={columns} perPage={Number(listCount[LIST_COUNT])} usePagenation onClickRow={onClickRow} rowKey={rowKey} frozenCount={1} height={tuiHeight} idxKey='cSeq' />
        </TuiGridWrapper>
      )}

      {size.innerSize.W <= 1024 && (
        <Portal openModal>
          <SearchFilterModal openModal={openModal} setOpenModal={setOpenModal} onClickSearch={handleSearch} onClickReset={onClickInitiateSearchOption}>
            <div className='filter-option-wrapper'>
              <div className='filter-option-col filter-underline filter-option-date'>
                <span className='calendarLabel'>{t('적용일자')}</span>
                <div className='inputForm-col'>
                  <div className='flex-basic' role='presentation' onClick={onClickRangeInput}>
                    <input
                      id='startInput'
                      type='text'
                      readOnly
                      value={userInputDateRange.start}
                      // onMouseDown={onClickRangeInput}
                      onChange={(e) => onChangeRangeInput(e, 'start', setDateRange, setUserInputDateRange)}
                    />
                    <span className='wave'> ~ </span>
                    <input id='endInput' type='text' readOnly value={userInputDateRange.end} onChange={(e) => onChangeRangeInput(e, 'end', setDateRange, setUserInputDateRange)} />
                  </div>
                  {visibleRangePicker && (
                    <div className='rangePickerWrapper'>
                      <RangePicker state={dateRange} setState={setDateRange} setVisible={setVisibleRangePicker} />
                    </div>
                  )}
                </div>
              </div>
              <div className='filter-option-cont'>
                <div className='filter-option-row'>
                  <label htmlFor='useYn'>{t('보기 설정')}</label>
                  <SelectBox
                    options={listCountComCdList}
                    defaultOption={listCount.cdName}
                    state={listCount}
                    setState={setListCount}
                    stateKey={LIST_COUNT}
                    initiateKey={listCount[LIST_COUNT]}
                    setTableLinesAPI={patchUserMenuAPI}
                    optionHeight='height-sm'
                    rsearch
                  />
                </div>
              </div>
            </div>
          </SearchFilterModal>
        </Portal>
      )}
    </>
  );
};

export default List;
